
import { toast } from 'sonner';
import { TimerType, TimerSettings } from './timerTypes';
import { playNotification } from './timerUtils';

export interface TimerCompletionProps {
  timerType: TimerType;
  sequencePosition: number;
  completedPomodoros: number;
  settings: TimerSettings;
  activeTask: any | null;
  updateTaskTime: (seconds: number, completedPomodoro?: boolean) => void;
  resetTimer: (type: TimerType, startRunning: boolean, sequencePos: number) => void;
  setIsFirstStart: (value: boolean) => void;
  setSessionIndex: (index: number) => void;
  user: any | null;
  finalizeCurrentEntry?: (status: 'completed' | 'interrupted') => Promise<number | undefined>;
}

export const handleTimerCompletion = async ({
  timerType,
  sequencePosition,
  completedPomodoros,
  settings,
  activeTask,
  updateTaskTime,
  resetTimer,
  setIsFirstStart,
  setSessionIndex,
  user,
  finalizeCurrentEntry
}: TimerCompletionProps) => {
  playNotification();
  
  const currentPos = sequencePosition;
  const nextPos = currentPos + 1;
  const maxPosition = settings.sessions * 2 - 1;
  
  if (timerType === 'pomodoro') {
    let actualDuration = 0;
    
    if (user && activeTask && finalizeCurrentEntry) {
      try {
        const duration = await finalizeCurrentEntry('completed');
        actualDuration = duration || (settings.pomodoro * 60);
      } catch (error) {
        console.error('Failed to finalize time entry:', error);
        actualDuration = settings.pomodoro * 60;
      }
    } else {
      actualDuration = settings.pomodoro * 60;
    }
    
    if (activeTask) {
      updateTaskTime(actualDuration, true);
    }
    
    const newCount = completedPomodoros + 1;
    
    if (newCount % settings.sessions === 0) {
      if (user) {
        toast.success(`Completed ${settings.sessions} Pomodoro sessions!`);
      } else {
        toast.success(`Completed ${settings.sessions} Pomodoro sessions! Create an account to track your progress.`);
      }
      
      setTimeout(() => {
        resetTimer('longBreak', true, maxPosition);
        setIsFirstStart(false);
      }, 1000);
    } else {
      toast.success('Pomodoro completed! Taking a short break.');
      
      const shortBreakPos = currentPos + 1;
      setTimeout(() => {
        resetTimer('shortBreak', true, shortBreakPos);
        setIsFirstStart(false);
      }, 1000);
      
      setSessionIndex(Math.floor(shortBreakPos / 2));
    }
    
    return newCount;
  } else {
    if (user && activeTask && finalizeCurrentEntry) {
      try {
        await finalizeCurrentEntry('completed');
      } catch (error) {
        console.error('Failed to finalize break entry:', error);
      }
    }
    
    toast.success(`${timerType === 'shortBreak' ? 'Short' : 'Long'} break completed!`);
    
    if (timerType === 'longBreak') {
      if (settings.autoRepeat) {
        setSessionIndex(0);
        setTimeout(() => {
          resetTimer('pomodoro', true, 0);
          setIsFirstStart(false);
        }, 1000);
      } else {
        setSessionIndex(0);
        setTimeout(() => {
          resetTimer('pomodoro', false, 0);
          setIsFirstStart(true);
        }, 1000);
      }
    } else if (nextPos <= maxPosition) {
      const nextPomodoro = Math.floor(nextPos / 2);
      setTimeout(() => {
        resetTimer('pomodoro', true, nextPos);
        setIsFirstStart(false);
        setSessionIndex(nextPomodoro);
      }, 1000);
    }
    
    return completedPomodoros;
  }
};
